// frontend/src/services/apiService.js
import { apiClient } from '../api/client';
import databaseClient from '../api/databaseClient';
import { getToken } from '../auth/auth';

// Helper function to handle API responses
const handleResponse = (response) => {
  if (response.data && response.data.success === false) {
    throw new Error(response.data.message || 'API request failed');
  }
  return response.data;
};

// Helper function to handle API errors
const handleError = (error) => {
  console.error('API Error:', error);
  
  if (error.response) {
    // The request was made and the server responded with a status code
    // that falls out of the range of 2xx
    const { status, data } = error.response;
    
    if (status === 401) {
      // Handle unauthorized access
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
      throw new Error('Session expired. Please log in again.');
    }
    
    if (status === 403) {
      throw new Error('You do not have permission to perform this action.');
    }
    
    if (status === 404) {
      throw new Error('The requested resource was not found.');
    }
    
    if (status === 500) {
      throw new Error('Server error. Please try again later.');
    }
    
    throw new Error(data.message || 'An error occurred while processing your request.');
  } else if (error.request) {
    // The request was made but no response was received
    throw new Error('Unable to connect to the server. Please check your internet connection.');
  } else {
    // Something happened in setting up the request that triggered an Error
    throw new Error('An unexpected error occurred. Please try again.');
  }
};

// Authentication endpoints
export const authService = {
  login: async (credentials) => {
    try {
      const response = await apiClient.post('/api/auth/login', credentials);
      return handleResponse(response);
    } catch (error) {
      return handleError(error);
    }
  },
  
  register: async (userData) => {
    try {
      const response = await apiClient.post('/api/auth/register', userData);
      return handleResponse(response);
    } catch (error) {
      return handleError(error);
    }
  },
  
  logout: async () => {
    try {
      const token = getToken();
      if (token) {
        await apiClient.post('/api/auth/logout');
      }
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    } catch (error) {
      console.error('Logout error:', error);
      // Still clear local storage and redirect even if the API call fails
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
  }
};

// Admin endpoints
export const adminService = {
  getLeads: async (params = {}) => {
    try {
      const response = await apiClient.get('/admin/leads', { params });
      return handleResponse(response);
    } catch (error) {
      return handleError(error);
    }
  },
  
  getTransactions: async (params = {}) => {
    try {
      const response = await apiClient.get('/admin/transactions', { params });
      return handleResponse(response);
    } catch (error) {
      return handleError(error);
    }
  },
  
  getDashboardStats: async () => {
    try {
      const response = await apiClient.get('/admin/dashboard/stats');
      return handleResponse(response);
    } catch (error) {
      return handleError(error);
    }
  }
};

// Agent endpoints
export const agentService = {
  getProfile: async () => {
    try {
      const response = await apiClient.get('/agent/profile');
      return handleResponse(response);
    } catch (error) {
      return handleError(error);
    }
  },
  
  updateProfile: async (profileData) => {
    try {
      const response = await apiClient.put('/agent/profile', profileData);
      return handleResponse(response);
    } catch (error) {
      return handleError(error);
    }
  },
  
  getLeads: async (params = {}) => {
    try {
      const response = await apiClient.get('/agent/leads', { params });
      return handleResponse(response);
    } catch (error) {
      return handleError(error);
    }
  }
};

// Database operations
export const databaseService = {
  query: async (query, params = []) => {
    try {
      const response = await databaseClient.post('/database/query', { query, params });
      return handleResponse(response);
    } catch (error) {
      return handleError(error);
    }
  },
  
  execute: async (query, params = []) => {
    try {
      const response = await databaseClient.post('/database/execute', { query, params });
      return handleResponse(response);
    } catch (error) {
      return handleError(error);
    }
  }
};

// Email service with all email-related functionality
export const emailService = {
  // Lead email notifications
  sendInitialLeadEmail: (leadId, agentId) => apiClient.post('/email/send-initial', { leadId, agentId }),
  sendFullLeadEmail: (leadId, agentEmail) => apiClient.post('/email/send-full', { leadId, agentEmail }),
  notifyAllAgentsInState: (leadId, state) => apiClient.post('/admin/email/notify-state', { leadId, state }),
  
  // Email history and preview
  getLeadEmailHistory: (leadId) => apiClient.get(`/email/history/${leadId}`),
  getEmailPreview: (emailType, leadId = null) => {
    let url = `/email/preview/${emailType}`;
    if (leadId) url += `?leadId=${leadId}`;
    return apiClient.get(url);
  },
  
  // Email management
  resendLeadEmails: (leadId) => apiClient.post(`/email/resend/${leadId}`),
  getEmailStats: () => apiClient.get('/email/stats')
};

export default apiClient; 